import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Typography, Skeleton } from '@mui/material';
import OncologySkeleton from '../../dashboard/triggers/skeleton';
import { OncologyBox, CustomReactSelect } from '../../../shared';
import { getFieldEngagementSegment } from '../../../../redux/thunks/engagementThunks';

function TriggerEngagement() {
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector(
        (state) => state.filter,
    );
    const {
        fieldTriggerEngagement,
        isLoading,
        fieldPhysicianSegments,
        isFieldTriggerEngagementLoading,
        completedEngagement,
        dismissedEngagement,
    } = useSelector((state) => state.fieldEngagement);
    const { companyConfig } = useSelector((state) => state.adminSettings);
    const { value } = companyConfig;
    const [hovered, setHovered] = useState(-1);
    const [fieldPhysicianSegment, setFieldPhysicianSegment] = useState(
        fieldPhysicianSegments[0] || { label: 'All', value: 'All' },
    );
    const [staticCompleted, setStaticCompleted] = useState([]);

    const finalLoading = !!(isLoading || isFieldTriggerEngagementLoading);
    const hideActioned = [161, 33, 150, 62, 16];
    const extrafields = [161, 33, 150, 140, 67, 62];
    const hideAvgTimeToRespond = [67, 140, 16];
    const hideSegmentBox = [67];
    const hideDismissed = [16];

    const Titles = {
        primary: 'Field Trigger Engagement (Sales Reps)',
        150: 'Field Trigger Engagement (MSL)',
    };

    const monthsStatic = {
        march: [
            {
                name: 'Email',
                count: 6,
                percent: '9.38',
            },
            {
                name: 'Phone',
                count: 57,
                percent: '89.06',
            },
            {
                name: 'Text',
                count: 1,
                percent: '1.56',
            },
        ],
        april: [
            {
                name: 'Email',
                count: 411,
                percent: '95.58',
            },
            {
                name: 'Phone',
                count: 17,
                percent: '3.95',
            },
            {
                name: 'Text',
                count: 2,
                percent: '0.47',
            },
        ],
        may: [
            {
                name: 'Email',
                count: 7,
                percent: '33.33',
            },
            {
                name: 'Phone',
                count: 14,
                percent: '66.67',
            },
            {
                name: 'Text',
                count: 0,
                percent: '0.00',
            },
        ],
        june: [
            {
                name: 'Email',
                count: 12,
                percent: '20.00',
            },
            {
                name: 'Phone',
                count: 14,
                percent: '23.33',
            },
            {
                name: 'Text',
                count: 34,
                percent: '56.67',
            },
        ],
        july: [
            {
                name: 'Email',
                count: 8,
                percent: '20.51',
            },
            {
                name: 'Phone',
                count: 5,
                percent: '12.82',
            },
            {
                name: 'Text',
                count: 26,
                percent: '66.67',
            },
        ],
        august: [
            {
                name: 'Email',
                count: 5,
                percent: '7.35',
            },
            {
                name: 'Phone',
                count: 3,
                percent: '4.41',
            },
            {
                name: 'Text',
                count: 60,
                percent: '88.24',
            },
        ],
        september: [
            {
                name: 'Email',
                count: 1,
                percent: '5.56',
            },
            {
                name: 'Phone',
                count: 1,
                percent: '5.56',
            },
            {
                name: 'Text',
                count: 16,
                percent: '88.89',
            },
        ],
        three_months: [
            {
                name: 'Email',
                count: 25,
                percent: '14.97',
            },
            {
                name: 'Phone',
                count: 22,
                percent: '13.17',
            },
            {
                name: 'Text',
                count: 120,
                percent: '71.86',
            },
        ],
        year: [
            {
                name: 'Email',
                count: 450,
                percent: '64.29',
            },
            {
                name: 'Phone',
                count: 96,
                percent: '13.71',
            },
            {
                name: 'Text',
                count: 154,
                percent: '22.00',
            },
        ],
    };

    const getStaticData = () => {
        if (!startDateRange || !endDateRange) return [];

        const start = new Date(startDateRange);
        const end = new Date(endDateRange);

        const msDiff = end.getTime() - start.getTime();
        const daysDiff = msDiff / (1000 * 60 * 60 * 24);

        // Return yearly data for 1–3 years
        if (daysDiff >= 330 && daysDiff <= 3 * 370) {
            return monthsStatic.year;
        }

        // Return 3-month data if close to 90 days (±5 days)
        if (daysDiff >= 85 && daysDiff <= 95) {
            return monthsStatic.three_months;
        }

        // Month mapping: 0 = Jan, 1 = Feb, ...
        const monthMap = {
            2: 'march',
            3: 'april',
            4: 'may',
            5: 'june',
            6: 'july',
            7: 'august',
            8: 'september',
        };

        const monthInRange = (monthIndex) => {
            const monthStart = new Date(2025, monthIndex, 1);
            const monthEnd = new Date(2025, monthIndex + 1, 0);
            return start <= monthEnd && end >= monthStart;
        };

        for (const [monthIndex, key] of Object.entries(monthMap)) {
            if (monthInRange(Number(monthIndex))) {
                return monthsStatic[key];
            }
        }

        return [];
    };

    const getTitle = () => Titles[company.id] || Titles.primary;

    // need to delete
    const updatedData = () => {
        if (fieldTriggerEngagement && company.id === 38) {
            return fieldTriggerEngagement.map((item) => {
                if (item.name === 'Overall Response Rate') {
                    return {
                        ...item,
                        volume: '0',
                        current: '0',
                        previousVolume: '0',
                    };
                } else {
                    return item;
                }
            });
        } else {
            return fieldTriggerEngagement;
        }
    };

    const result = updatedData();

    const MouseEnter = (index) => setHovered(index);
    const MouseLeave = () => setHovered(-1);

    const handleSegmentChange = (option) => {
        if (option) {
            setFieldPhysicianSegment(option);
            dispatch(
                getFieldEngagementSegment({
                    companyId: company.id,
                    segment: option.value
                        .replace(/[^a-zA-Z0-9]/g, '_')
                        .toLowerCase(),
                    startRange: startDateRange,
                    endRange: endDateRange,
                    //isUnified: value?.toLowerCase() === 'true' || false
                }),
            );
        }
    };
    const isConfigReady =
        companyConfig && Object.keys(companyConfig).length > 0;
    useEffect(() => {
        if (company.id || isConfigReady) {
            setFieldPhysicianSegment({ label: 'All', value: 'All' });
            dispatch(
                getFieldEngagementSegment({
                    companyId: company.id,
                    segment: 'all',
                    startRange: startDateRange,
                    endRange: endDateRange,
                    //isUnified: value?.toLowerCase() === 'true' || false
                }),
            );
        }
    }, [company, startDateRange, endDateRange, isConfigReady]);

    useEffect(() => {
        if (company.id === 67) {
            const data = getStaticData();
            setStaticCompleted(data);
        }
    }, [startDateRange, endDateRange, company.id]);
    return (
        <Box className="box-sizing-border-box">
            <Box className="flex-row-space-between-center pb-24px">
                <Typography className="text-lg-semi-bold gray-900">
                    {getTitle()}
                </Typography>
                {finalLoading ? (
                    <Skeleton height={60} variant="text" width={250} />
                ) : hideSegmentBox.includes(company.id) ? null : (
                    <Box className="w-40">
                        <CustomReactSelect
                            handleFilter={handleSegmentChange}
                            label="Select Physician Segment"
                            options={fieldPhysicianSegments}
                            placeholder="Select Segment"
                            selectedOption={fieldPhysicianSegment}
                        />
                    </Box>
                )}
            </Box>
            <Grid container spacing={2}>
                {finalLoading
                    ? Array.from({ length: 5 }).map((_, index) => (
                          <Grid item key={'trigger-skeleton' + index} xs={4}>
                              <OncologySkeleton />
                          </Grid>
                      ))
                    : result?.map((data, index) => {
                          const current = data?.current || 0;
                          const previous = data?.previousVolume || 0;
                          const change =
                              previous === 0 && current === 0
                                  ? 0
                                  : ((current - previous) * 100) / previous;

                          if (
                              hideActioned.includes(company.id) &&
                              data.name === 'Actioned'
                          )
                              return null;
                          if (
                              hideAvgTimeToRespond.includes(company.id) &&
                              data.name === 'Average Time to Respond (days)'
                          )
                              return null;
                          if (
                              hideDismissed.includes(company.id) &&
                              data.name === 'Dismissed'
                          )
                              return null;

                          return (
                              <Grid
                                  item
                                  key={'trigger' + index}
                                  xs={6}
                                  onMouseEnter={() => MouseEnter(index)}
                                  onMouseLeave={MouseLeave}
                              >
                                  <OncologyBox
                                      a={
                                          company.id === 67
                                              ? []
                                              : dismissedEngagement
                                      }
                                      b={
                                          company.id === 67
                                              ? staticCompleted
                                              : completedEngagement
                                      }
                                      boxData={data}
                                      change={change}
                                      extraFields={extrafields.includes(
                                          company.id,
                                      )}
                                      isRating={true}
                                      oncologyId={index}
                                      selectedOncologyId={hovered}
                                      type="fieldEngagement"
                                  />
                              </Grid>
                          );
                      })}
            </Grid>
        </Box>
    );
}

export default TriggerEngagement;
